import api from './api';

/**
 * A single order field/tab display rule from `order_field_display_rules`.
 * DISPLAY layer only (hide / readonly by movetype + invoiced) — orthogonal to
 * the write-permission layer (order-lc-fields / leg-editing), which is in code.
 */
export interface OrderFieldDisplayRule {
  targetType: 'field' | 'tab';
  target: string; // field: legacy DB column name; tab: tab id
  scope: string; // movetype | 'INVOICED' | 'ALL'
  behavior: 'hidden' | 'readonly';
  /** 'Y' = a pro-admin-override holder may unlock this rule on one pro for one
   *  session (#33501). Only meaningful on a `readonly` rule.
   *
   *  OPTIONAL, and absent reads as 'N'. Migrations are hand-applied per DB, so a
   *  frontend deployed ahead of migration 449 gets rows without the column — and the
   *  safe reading of "I don't know" for a bypass flag is "no". */
  overridable?: 'Y' | 'N';
  system: string; // 'cms' | 'template'
  note?: string | null;
}

// The rules are global (shared across every order) and change rarely, so we
// fetch once and cache the promise for the app's lifetime. Provider-independent
// (no react-query dependency) so it works everywhere the order form renders —
// the /pro page, the dispatch drawer, and isolated test/preview trees.
let rulesPromise: Promise<OrderFieldDisplayRule[]> | null = null;

/**
 * Fetch all active order field/tab display rules (cached). `system` defaults to
 * 'cms' (orders); templates are a later pass.
 */
export function fetchOrderFieldDisplayRules(
  system: string = 'cms',
): Promise<OrderFieldDisplayRule[]> {
  if (!rulesPromise) {
    rulesPromise = api
      .get<OrderFieldDisplayRule[]>('/order-field-display-rules', { params: { system } })
      .then((res) => res.data || [])
      .catch((err) => {
        // On failure, reset so a later mount can retry, and fall back to
        // "no rules" (everything shown + editable — the safe default that
        // preserves today's behavior rather than hiding fields on a blip).
        rulesPromise = null;
        console.error('Failed to load order field display rules:', err);
        return [];
      });
  }
  return rulesPromise;
}

/** Test/HMR aid — drop the cache so the next fetch re-hits the API. */
export function _resetOrderFieldDisplayRulesCache(): void {
  rulesPromise = null;
}
